import { Children, createContext, isValidElement, useContext, useEffect, useState, type AnchorHTMLAttributes, type ReactElement, type ReactNode } from "react";

type RouterValue = { pathname: string; navigate: (to: string, replace?: boolean) => void };
const RouterContext = createContext<RouterValue | null>(null);

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => { const update = () => setPathname(window.location.pathname); addEventListener("popstate", update); return () => removeEventListener("popstate", update); }, []);
  const navigate = (to: string, replace = false) => { if (!to.startsWith("/")) return; history[replace ? "replaceState" : "pushState"]({}, "", to); setPathname(window.location.pathname); scrollTo({ top: 0 }); };
  return <RouterContext.Provider value={{ pathname, navigate }}>{children}</RouterContext.Provider>;
}

function useRouter() { const value = useContext(RouterContext); if (!value) throw new Error("Router context missing"); return value; }
export const useLocation = () => ({ pathname: useRouter().pathname });
export const useNavigate = () => useRouter().navigate;
export function useParams(): Record<string, string> { const parts = useRouter().pathname.split("/").filter(Boolean); return parts[0] === "automations" && parts[1] ? { id: decodeURIComponent(parts[1]) } : {}; }

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string };
export function Link({ to, onClick, ...props }: LinkProps) {
  const { navigate } = useRouter();
  return <a href={to} {...props} onClick={(event) => { onClick?.(event); if (!event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) { event.preventDefault(); navigate(to); } }} />;
}
export function NavLink({ to, className, end = false, ...props }: LinkProps & { end?: boolean }) {
  const { pathname } = useRouter(); const active = pathname === to || (!end && to !== "/" && pathname.startsWith(`${to}/`));
  return <Link to={to} {...props} className={`${className ?? ""}${active ? " active" : ""}`.trim()} />;
}

export function Route(_props: { path: string; element: ReactElement }) { return null; }
function matches(pattern: string, pathname: string): boolean {
  if (pattern === "*") return true;
  const expected = pattern.split("/").filter(Boolean), actual = pathname.split("/").filter(Boolean);
  return expected.length === actual.length && expected.every((part, index) => part.startsWith(":") || part === actual[index]);
}
export function Routes({ children }: { children: ReactNode }) {
  const { pathname } = useRouter();
  for (const child of Children.toArray(children)) if (isValidElement<{ path: string; element: ReactElement }>(child) && matches(child.props.path, pathname)) return child.props.element;
  return null;
}
export function Navigate({ to, replace = false }: { to: string; replace?: boolean }) { const navigate = useNavigate(); useEffect(() => navigate(to, replace), [to, replace]); return null; }
